/**
 * Boli Live - Notification Store (Zustand)
 *
 * Global notification state management for real-time alerts.
 * Wraps @boli/core NotificationService for web-specific state.
 */

import { create } from 'zustand';
import { getNotificationService } from '@boli/core';
import type { Notification } from '@boli/types';

interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  fetchNotifications: (params?: { limit?: number; offset?: number; unreadOnly?: boolean }) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  initialize: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isInitialized: false,

  // Initialize notification state
  initialize: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true });
    try {
      await get().refreshUnreadCount();
      await get().fetchNotifications({ limit: 10 });
      set({ isInitialized: true, isLoading: false });
    } catch (error) {
      console.error('Notification initialization failed:', error);
      set({ isLoading: false });
    }
  },

  // Fetch notifications
  fetchNotifications: async (params) => {
    set({ isLoading: true });
    try {
      const notificationService = getNotificationService();
      const response = await notificationService.getNotifications(params);
      set({
        notifications: response.notifications,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      set({ isLoading: false });
    }
  },

  // Refresh unread count
  refreshUnreadCount: async () => {
    try {
      const notificationService = getNotificationService();
      const count = await notificationService.getUnreadCount();
      set({ unreadCount: count });
    } catch (error) {
      console.error('Failed to refresh unread count:', error);
    }
  },

  // Mark single notification as read
  markAsRead: async (id: string) => {
    try {
      const notificationService = getNotificationService();
      const success = await notificationService.markAsRead(id);

      if (success) {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, isRead: true } : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    try {
      const notificationService = getNotificationService();
      const result = await notificationService.markAllAsRead();

      if (result.success) {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        }));
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  // Add a new notification (for real-time updates)
  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification.isRead ? 0 : 1),
    }));
  },
}));
